import { useState } from 'react';
import type { Dayjs } from 'dayjs';
import { DownloadIcon, EyeIcon, FileIcon } from 'lucide-react';
import { ReactComponent as PdfIcon } from '@rocco/icons/svg/pdf.svg';
import { cn } from '@rocco/utils/cn';
import { Spinner } from '@rocco/components/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { isImageFile, isPdfFile } from '~/modules/file';
import type { MessageAttachment } from '../../types';
import { formatDateFull } from '../../utils';
import { ChatLightbox, type LightboxItem } from '../lightbox/ChatLightbox';
import { ChatMessageAttachmentActions } from './ChatMessageAttachmentActions';

/**
 * A list of files in a chat message with file attachments.
 * {@link ChatMessageBubble}
 */
export function ChatMessageAttachments({
    className,
    attachments,
    date,
    isOutgoing,
    isOptimistic,
    tooltipDelayMs,
}: {
    className?: string;
    attachments: MessageAttachment[];
    date: Dayjs;
    isOutgoing: boolean;
    isOptimistic: boolean;
    tooltipDelayMs: number;
}) {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    // Lightbox items for images and PDFs.
    const lightboxItems = attachments
        .filter(({ FileName }) => isImageFile(FileName) || isPdfFile(FileName))
        .map<LightboxItem>((att) => ({
            id: att.ID,
            url: att.Link,
            filename: att.FileName ?? '',
            date,
        }));

    function openLightbox(id: number) {
        const index = lightboxItems.findIndex((item) => item.id === id);
        if (index >= 0) {
            setLightboxIndex(index);
            setLightboxOpen(true);
        }
    }

    return (
        <>
            <ul className={cn('flex flex-col gap-[8px]', className)}>
                {attachments.map(({ ID, Link, FileName }) =>
                    isImageFile(FileName) ? (
                        <ImageAttachment
                            key={ID}
                            url={Link}
                            filename={FileName ?? ''}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                            onClick={() => openLightbox(ID)}
                        />
                    ) : isPdfFile(FileName) ? (
                        <PdfAttachment
                            key={ID}
                            url={Link}
                            filename={FileName ?? ''}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                            onClick={() => openLightbox(ID)}
                        />
                    ) : (
                        <FileAttachment
                            key={ID}
                            url={Link}
                            filename={FileName ?? ''}
                            date={date}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                            tooltipDelayMs={tooltipDelayMs}
                        />
                    )
                )}
            </ul>
            {lightboxItems.length > 0 && (
                <ChatLightbox
                    open={lightboxOpen}
                    index={lightboxIndex}
                    items={lightboxItems}
                    onOpenChange={setLightboxOpen}
                    onIndexChange={setLightboxIndex}
                />
            )}
        </>
    );
}

/**
 * A single file attachment in {@link ChatMessageAttachments}.
 */
function FileAttachment({
    url,
    filename,
    date,
    isOutgoing,
    isOptimistic,
    tooltipDelayMs,
}: {
    url: string;
    filename: string;
    date: Dayjs;
    isOutgoing: boolean;
    isOptimistic: boolean;
    tooltipDelayMs: number;
}) {
    const tooltipDateString = formatDateFull(date);

    return (
        <TooltipProvider delayDuration={tooltipDelayMs}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <li
                        className={cn(
                            'relative w-full overflow-hidden rounded-[8px] text-[12px] leading-normal tracking-normal',
                            isOutgoing
                                ? 'bg-black/40 text-white'
                                : 'bg-white text-black'
                        )}
                    >
                        <a
                            className={cn(
                                'flex items-center gap-[8px] p-[8px]',
                                isOutgoing
                                    ? 'hover:bg-black/30 focus-visible:bg-black/30'
                                    : 'hover:bg-black/20 focus-visible:bg-black/20'
                            )}
                            href={url}
                            target="_blank"
                            rel="noopener"
                            download
                        >
                            <FileIcon className="size-[48px] shrink-0 p-[12px]" />
                            <div className="min-w-0 flex-1">
                                <div className="line-clamp-2 break-words font-medium">
                                    {filename}
                                </div>
                                <div className="flex items-center gap-[4px] opacity-70">
                                    <DownloadIcon className="size-[12px]" />
                                    <span>Download</span>
                                </div>
                            </div>
                        </a>
                        {isOptimistic && (
                            <div className="absolute inset-0 grid place-items-center bg-black/50">
                                <Spinner className="size-[20px] text-white" />
                            </div>
                        )}
                    </li>
                </TooltipTrigger>
                <TooltipContent side={isOutgoing ? 'right' : 'left'}>
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

/**
 * A single PDF attachment in {@link ChatMessageAttachments}.
 */
function PdfAttachment({
    url,
    filename,
    isOutgoing,
    isOptimistic,
    onClick,
}: {
    url: string;
    filename: string;
    isOutgoing: boolean;
    isOptimistic: boolean;
    onClick?: () => void;
}) {
    return (
        <li
            className={cn(
                'group relative w-full overflow-hidden rounded-[8px] text-[12px] leading-normal tracking-normal',
                isOutgoing ? 'bg-black/40 text-white' : 'bg-white text-black'
            )}
        >
            <button
                className={cn(
                    'flex w-full cursor-zoom-in items-center gap-[8px] p-[8px] text-left disabled:cursor-default',
                    isOutgoing
                        ? 'hover:bg-black/30 focus-visible:bg-black/30'
                        : 'hover:bg-black/20 focus-visible:bg-black/20'
                )}
                disabled={isOptimistic}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick?.();
                    }
                }}
            >
                <PdfIcon className="size-[48px] shrink-0 p-[4px]" />
                <div className="min-w-0 flex-1">
                    <div className="line-clamp-2 break-words font-medium">
                        {filename}
                    </div>
                    <div className="flex items-center gap-[4px] opacity-70">
                        <EyeIcon className="size-[12px]" />
                        <span>View</span>
                    </div>
                </div>
            </button>
            {!isOptimistic && (
                <ChatMessageAttachmentActions
                    className="absolute right-[6px] top-[6px] opacity-0 transition-opacity duration-100 group-hover:opacity-100"
                    url={url}
                    filename={filename}
                />
            )}
            {isOptimistic && (
                <div className="absolute inset-0 grid place-items-center bg-black/50">
                    <Spinner className="size-[20px] text-white" />
                </div>
            )}
        </li>
    );
}

/**
 * A single image attachment in {@link ChatMessageAttachments}.
 */
function ImageAttachment({
    url,
    filename,
    isOutgoing,
    isOptimistic,
    onClick,
}: {
    url: string;
    filename: string;
    isOutgoing: boolean;
    isOptimistic: boolean;
    onClick?: () => void;
}) {
    return (
        <li
            className={cn(
                'group relative w-full overflow-hidden rounded-[8px] text-[12px] leading-normal tracking-normal',
                isOutgoing ? 'bg-black/40 text-white' : 'bg-white text-black'
            )}
        >
            <button
                className={cn(
                    'flex w-full cursor-zoom-in items-center gap-[8px] p-[8px] text-left disabled:cursor-default',
                    isOutgoing
                        ? 'hover:bg-black/30 focus-visible:bg-black/30'
                        : 'hover:bg-black/20 focus-visible:bg-black/20'
                )}
                disabled={isOptimistic}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick?.();
                    }
                }}
            >
                <img
                    className="size-[48px] shrink-0 object-cover"
                    src={url}
                    alt={filename}
                    loading="lazy"
                />
                <div className="min-w-0 flex-1">
                    <div className="line-clamp-2 break-words font-medium">
                        {filename}
                    </div>
                    <div className="flex items-center gap-[4px] opacity-70">
                        <EyeIcon className="size-[12px]" />
                        <span>View</span>
                    </div>
                </div>
            </button>
            {!isOptimistic && (
                <ChatMessageAttachmentActions
                    className="absolute right-[6px] top-[6px] opacity-0 transition-opacity duration-100 group-hover:opacity-100"
                    url={url}
                    filename={filename}
                />
            )}
            {isOptimistic && (
                <div className="absolute inset-0 grid place-items-center bg-black/50">
                    <Spinner className="size-[20px] text-white" />
                </div>
            )}
        </li>
    );
}
